import { createContext, useContext, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import frMessages from "./fr.json";
import enMessages from "./en.json";

export const DEFAULT_LOCALE = "fr";
export const LANGUAGE_STORAGE_KEY = "site-language";

const messagesByLocale = {
  en: enMessages,
  fr: frMessages,
};

const LanguageContext = createContext(null);

const normalizeLocale = (value) => (value === "en" ? "en" : "fr");

const getInitialLocale = () => {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  try {
    return normalizeLocale(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_LOCALE;
  }
};

export const LanguageProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getInitialLocale);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
    } catch {
      // Ignore storage failures and keep the in-memory locale.
    }
    document.documentElement.lang = locale;
    return undefined;
  }, [locale]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        setLocaleState(normalizeLocale(event.newValue));
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const setLocale = (nextLocale) => {
    setLocaleState(normalizeLocale(nextLocale));
  };

  return (
    <LanguageContext.Provider value={{ locale, setLocale }}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messagesByLocale[locale] || messagesByLocale[DEFAULT_LOCALE]}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};
